'use client';

import { c as _c } from "react/compiler-runtime";
import type { GraphQLCommitment, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCardExpiryStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { commitmentsQuery, paymentMethodsQuery, supporterAddressQuery } from '../queries.ts';
import { getSupporterAddresses } from '../utils/addresses.ts';
import { getCommitmentsByPaymentMethodId } from '../utils/commitments.ts';
import { useSupporterQuery } from './base.ts';
import { hasSupporterDataIssues, useSupporterDataStatus } from './data-status.ts';

/**
 * Hook to get payment methods with associated commitments
 * @returns An object containing the payment methods and associated commitments.
 */
export const usePaymentMethodsWithCommitments = () => {
  const $ = _c(18);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [paymentMethodsQuery, commitmentsQuery];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    isLoading,
    isFetching,
    isPending,
    isError
  } = useSupporterQuery(t0);
  const [t2, t3] = t1;
  let t4;
  if ($[1] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[1] = t2;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const paymentMethods = t4;
  let t5;
  if ($[3] !== t3) {
    t5 = t3 === undefined ? [] : t3;
    $[3] = t3;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const commitments = t5;
  const {
    data: dataStatus
  } = useSupporterDataStatus();
  let t6;
  if ($[5] !== commitments) {
    t6 = getCommitmentsByPaymentMethodId(commitments);
    $[5] = commitments;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const commitmentsByPaymentMethodId = t6;
  let t7;
  if ($[7] !== dataStatus || $[8] !== isError) {
    t7 = isError || hasSupporterDataIssues(dataStatus, ["paymentMethods"]);
    $[7] = dataStatus;
    $[8] = isError;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const hasError = t7;
  const t8 = isLoading || isFetching || isPending;
  let t9;
  if ($[10] !== paymentMethods) {
    t9 = paymentMethods ?? [];
    $[10] = paymentMethods;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== commitments || $[13] !== commitmentsByPaymentMethodId || $[14] !== hasError || $[15] !== t8 || $[16] !== t9) {
    t10 = {
      isLoading: t8,
      hasError,
      paymentMethods: t9,
      commitmentsByPaymentMethodId,
      commitments
    };
    $[12] = commitments;
    $[13] = commitmentsByPaymentMethodId;
    $[14] = hasError;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
};

/**
 * Hook to get payment methods that have issues to resolve
 * @returns An object containing the payment methods and associated commitments.
 */
export const usePaymentMethodsWithIssues = () => {
  const $ = _c(9);
  const {
    isLoading,
    hasError,
    paymentMethods,
    commitmentsByPaymentMethodId
  } = usePaymentMethodsWithCommitments();
  let associatedCommitments;
  let paymentMethodsWithIssues;
  if ($[0] !== commitmentsByPaymentMethodId || $[1] !== hasError || $[2] !== paymentMethods) {
    paymentMethodsWithIssues = [];
    associatedCommitments = [];
    if (!hasError && paymentMethods.length > 0) {
      paymentMethodsWithIssues = paymentMethods.filter(_temp);
    }
    if (paymentMethodsWithIssues.length > 0 && commitmentsByPaymentMethodId) {
      for (const paymentMethod of paymentMethodsWithIssues) {
        const commitments = commitmentsByPaymentMethodId[paymentMethod.id ?? ""];
        if (commitments?.length) {
          associatedCommitments.push(...commitments);
        }
      }
    }
    $[0] = commitmentsByPaymentMethodId;
    $[1] = hasError;
    $[2] = paymentMethods;
    $[3] = associatedCommitments;
    $[4] = paymentMethodsWithIssues;
  } else {
    associatedCommitments = $[3];
    paymentMethodsWithIssues = $[4];
  }
  let t0;
  if ($[5] !== associatedCommitments || $[6] !== isLoading || $[7] !== paymentMethodsWithIssues) {
    t0 = {
      isLoading,
      paymentMethodsWithIssues,
      associatedCommitments
    };
    $[5] = associatedCommitments;
    $[6] = isLoading;
    $[7] = paymentMethodsWithIssues;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  return t0;
};
export const useSupporterBillingAddresses = () => {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [supporterAddressQuery, paymentMethodsQuery];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1,
    isLoading,
    isFetching,
    isPending,
    isError
  } = useSupporterQuery(t0);
  const [supporterAddress, supporterPaymentMethods] = t1;
  let t2;
  if ($[1] !== supporterAddress || $[2] !== supporterPaymentMethods) {
    t2 = supporterAddress ? getSupporterAddresses(supporterAddress, supporterPaymentMethods ?? []) : undefined;
    $[1] = supporterAddress;
    $[2] = supporterPaymentMethods;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isError || $[5] !== isFetching || $[6] !== isLoading || $[7] !== isPending || $[8] !== t2) {
    t3 = {
      supporterAddresses: t2,
      isLoading,
      isFetching,
      isPending,
      isError
    };
    $[4] = isError;
    $[5] = isFetching;
    $[6] = isLoading;
    $[7] = isPending;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
function _temp(t0) {
  const {
    creditCard
  } = t0;
  return creditCard?.expiryStatus === GraphQLCardExpiryStatus.Expired;
}